"use client";

import React from "react";
import { CircleCheck, Clock, Shield, Zap, Loader, Headphones } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const benefits = [{
  icon: Clock,
  title: "Rapid Execution",
  description: "Execute high-volume orders with sub-millisecond latency and precision."
}, {
  icon: Zap,
  title: "Deep Liquidity",
  description: "Access institutional-grade liquidity pools across multiple asset classes."
}, {
  icon: Shield,
  title: "Cyber-Tech Security",
  description: "Military-grade encryption and multi-sig protocols for asset protection."
}, {
  icon: CircleCheck,
  title: "Global Compliance",
  description: "Fully regulated frameworks adhering to international financial standards."
}];
function SupportForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="138473" className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-sm bg-primary/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-foreground tracking-tight">Inquiry Received</h3>
        <p className="text-muted-foreground max-w-sm">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4 border-primary/50 hover:bg-primary/10 rounded-sm">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Alexander Sterling" className="bg-background/50 border-border/50 focus-visible:ring-primary/50 rounded-sm" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">Enter the name of the primary contact person.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Corporate email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid business email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Corporate Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="sterling@institutional.com" className="bg-background/50 border-border/50 focus-visible:ring-primary/50 rounded-sm" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">We will use this for official correspondence.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^\+?[0-9\s\-()]{7,20}$/,
          message: "Please enter a valid contact number"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Contact Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className="bg-background/50 border-border/50 focus-visible:ring-primary/50 rounded-sm" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">Include country code for international calls.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="order_number" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-medium">Organization Name</FormLabel>
              <FormControl>
                <Input placeholder="Sterling Capital Partners" className="bg-background/50 border-border/50 focus-visible:ring-primary/50 rounded-sm" {...field} />
              </FormControl>
              <FormDescription className="text-xs opacity-70">The legal entity requesting funding access.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="product" rules={{
      required: "Account type selection is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-medium">Institutional Account Type</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="bg-background/50 border-border/50 focus-visible:ring-primary/50 rounded-sm">
                  <SelectValue placeholder="Select account category" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Smartphone">Hedge Fund</SelectItem>
                <SelectItem value="Laptop">Family Office</SelectItem>
                <SelectItem value="Television">Asset Manager</SelectItem>
                <SelectItem value="Accessory">Corporate Treasury</SelectItem>
                <SelectItem value="Other">Other Institutional</SelectItem>
              </SelectContent>
            </Select>
            <FormDescription className="text-xs opacity-70">Choose the category that best describes your entity.</FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="issue_description" rules={{
      required: "Funding requirements are required",
      minLength: {
        value: 20,
        message: "Please provide more detail (min 20 characters)"
      },
      maxLength: {
        value: 1000,
        message: "Maximum 1000 characters allowed"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-medium">Funding Requirements</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your capital allocation needs and trading objectives..." className="min-h-[120px] bg-background/50 border-border/50 focus-visible:ring-primary/50 resize-none rounded-sm" {...field} />
            </FormControl>
            <FormDescription className="text-xs opacity-70">Detail your expected volume and specific market requirements.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-sm flex items-start gap-3">
          <Shield className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive/90">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-bold uppercase tracking-wider rounded-sm shadow-[0_0_20px_rgba(var(--primary),0.3)] hover:shadow-[0_0_30px_rgba(var(--primary),0.5)] transition-all duration-400 ease-[cubic-bezier(0.4,0,0.2,1)]">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : "Submit Institutional Inquiry"}
      </Button>
    </Form>;
}
export default function TechSupportInquiry() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="funding-inquiry" className="dark bg-background text-foreground py-24 relative overflow-hidden" dir="ltr">
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[30rem] h-[30rem] bg-accent/10 rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {}
          <div ref={leftRef} className={`space-y-10 transition-all duration-1000 ease-out ${isLeftVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-primary/10 border border-primary/20 text-primary text-sm font-bold uppercase tracking-widest mb-2">
                <Headphones className="size-4" />
                <span>Institutional Funding</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-black tracking-tighter text-foreground">
                Scale your capital with <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/60">TradeSphere</span> infrastructure.
              </h2>
              <p className="text-lg text-muted-silver max-w-xl leading-relaxed font-medium">
                At TradeSphere, we provide elite institutions with the liquidity, security, and execution technology required for high-stakes market operations. Partner with us to optimize your trading ecosystem.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 pt-4">
              {benefits.map((benefit, i) => <div key={i} data-index={i} className="flex gap-4 items-start">
                  <div className="size-12 rounded-sm bg-card border border-border/50 flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(var(--primary),0.1)]">
                    <benefit.icon className="size-6 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-bold text-foreground mb-1 tracking-tight">{benefit.title}</h4>
                    <p className="text-sm text-muted-silver leading-relaxed">{benefit.description}</p>
                  </div>
                </div>)}
            </div>
          </div>

          {}
          <div ref={rightRef} className={`transition-all duration-1000 delay-200 ease-out ${isRightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative group">
              {}
              <div className="absolute -inset-0.5 bg-gradient-to-br from-primary/40 to-accent/40 rounded-sm blur-md opacity-30 group-hover:opacity-60 transition duration-500"></div>
              
              <div className="relative bg-card/60 backdrop-blur-xl border border-border/50 rounded-sm p-8 shadow-2xl">
                <div className="mb-8">
                  <h3 className="text-2xl font-black text-foreground mb-2 tracking-tight uppercase">Institutional Inquiry</h3>
                  <p className="text-sm text-muted-silver font-medium">
                    Provide your institutional details below. Our capital markets team will review and respond within 24 hours.
                  </p>
                </div>

                <FormProvider formKey="funding_inquiry_form_funding" formId="funding_inquiry_form_funding" sectionName="Funding Inquiry" defaultValues={{
                name: "",
                email: "",
                phone: "",
                order_number: "",
                product: "",
                issue_description: ""
              }} submitText="Submit Institutional Inquiry" successMessage="Inquiry received. A TradeSphere relationship manager will contact your organization shortly." errorMessage="Submission failed. Please verify your details and attempt re-submission.">
                  <SupportForm />
                </FormProvider>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}